import { t } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { MerchantInfo, MerchantStatus } from 'services/merchants/types';

import { getDefaultDatePresets } from 'components/filters/date/keys';
import { CheckboxFilterPassedProps, DateFilterPassedProps, RadioFilterPassedProps } from 'components/filters/types';

import { Filter, FilterSection, FilterType } from 'types/filters';

import { COLUMN_IDS, LABELS } from './keys';
import { Filters, MerchantDataRow, RequestFilters } from './types';

export const transformAccountsListDataToTableFormat = (accountRecordInfos?: MerchantInfo[]): MerchantDataRow[] => {
  return accountRecordInfos
    ? accountRecordInfos.map((accountRecordInfo) => ({
        ...accountRecordInfo,
        id: accountRecordInfo.uuid,
      }))
    : [];
};

export const makeFilterSections = (filterMap: Record<string, Filter<unknown>>): FilterSection[] => {
  return [
    {
      key: 'filters',
      content: [filterMap.status, filterMap.date, filterMap.hasKycErrors, filterMap.products],
    },
  ];
};

export const makeFilters = (): Record<string, Filter<unknown>> => {
  const datePresets = getDefaultDatePresets();
  const statusFilter: Filter<RadioFilterPassedProps> = {
    key: COLUMN_IDS.STATUS,
    label: t(LABELS.FILTERS.STATUS.LABEL),
    type: FilterType.Radio,
    options: [
      {
        key: MerchantStatus.Active,
        value: MerchantStatus.Active,
        label: t(LABELS.FILTERS.STATUS.ACTIVE),
      },
      {
        key: MerchantStatus.Onboarding,
        value: MerchantStatus.Onboarding,
        label: t(LABELS.FILTERS.STATUS.ONBOARDING),
      },
      {
        key: MerchantStatus.Deactivated,
        value: MerchantStatus.Deactivated,
        label: t(LABELS.FILTERS.STATUS.DEACTIVATED),
      },
      {
        key: MerchantStatus.Inactive,
        value: MerchantStatus.Inactive,
        label: t(LABELS.FILTERS.STATUS.INACTIVE),
      },
    ],
  };

  const dateFilter: Filter<DateFilterPassedProps> = {
    key: 'date',
    label: t(LABELS.FILTERS.DATE.LABEL),
    type: FilterType.Date,
    presets: datePresets,
  };

  const hasKycErrorsFilter: Filter<CheckboxFilterPassedProps> = {
    key: 'hasKycErrors',
    label: t(LABELS.FILTERS.HAS_ERROR.LABEL),
    type: FilterType.Checkbox,
    options: [
      {
        key: 'hasKycErrors',
        value: 'hasKycErrors',
        label: t(LABELS.FILTERS.HAS_ERROR.ERROR),
      },
    ],
  };

  const productFilters: Filter<CheckboxFilterPassedProps> = {
    key: 'products',
    label: t(LABELS.FILTERS.PRODUCTS.LABEL),
    type: FilterType.Checkbox,
    options: [
      {
        key: 'pwcEnabled',
        value: 'pwcEnabled',
        label: t(LABELS.FILTERS.PRODUCTS.OPTIONS.PAY_WITH_CRYPTO),
      },
      {
        key: 'cryptoPayoutEnabled',
        value: 'cryptoPayoutEnabled',
        label: t(LABELS.FILTERS.PRODUCTS.OPTIONS.CRYPTO_PAYOUTS),
      },
      {
        key: 'stablecoinSettlementEnabled',
        value: 'stablecoinSettlementEnabled',
        label: t(LABELS.FILTERS.PRODUCTS.OPTIONS.STABLECOIN_SETTLEMENT),
      },
    ],
  };

  const reducedFilters = [dateFilter, statusFilter, hasKycErrorsFilter, productFilters].reduce((acc, cur) => {
    return { ...acc, [cur.key]: cur };
  }, {});

  return {
    ...reducedFilters,
  };
};

export const prepareFilters = (filters: Filters): RequestFilters | undefined => {
  if (!filters) {
    return undefined;
  }

  const { date, status, hasKycErrors } = filters;
  const pwcEnabled = filters.products?.includes('pwcEnabled');
  const stablecoinSettlementEnabled = filters.products?.includes('stablecoinSettlementEnabled');
  const cryptoPayoutEnabled = filters.products?.includes('cryptoPayoutEnabled');

  const nextFilters: RequestFilters = {};

  if (pwcEnabled) {
    nextFilters.pwcEnabled = true;
  }

  if (stablecoinSettlementEnabled) {
    nextFilters.stablecoinSettlementEnabled = true;
  }

  if (cryptoPayoutEnabled) {
    nextFilters.cryptoPayoutEnabled = true;
  }

  if (date) {
    nextFilters.date = date;
  }

  if (status && !isEmpty(status)) {
    const [value] = status;
    nextFilters.status = value;
  }

  if (hasKycErrors !== undefined) {
    nextFilters.hasKycErrors = Array.isArray(hasKycErrors) ? hasKycErrors.includes('hasKycErrors') : Boolean(hasKycErrors);
  }

  return nextFilters;
};
